import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, LogIn, AlertCircle, KeyRound, MailCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

/**
 * Dedicated admin login page, deliberately *not* wired to Google auth so
 * the platform-owner has a separate credential path that survives even if
 * the OAuth session fails to resolve as admin.
 *
 * Modes:
 *   `signin` — email + password (+ optional 2FA step-up)
 *   `forgot` — request a reset code via Resend e-mail
 *   `reset`  — enter received code + new password → auto-login
 *
 * Route: `/admin/login` (see App.js).
 */
export default function AdminLogin() {
  const nav = useNavigate();
  const { user, checkAuth } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  // Step-up state — set when the /login response signals two_fa_required.
  const [twoFaToken, setTwoFaToken] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  // Reset-flow state
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Already an admin? Skip straight to /admin.
  useEffect(() => {
    if (user?.is_admin) nav("/admin", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      if (data?.two_fa_required && data?.two_fa_token) {
        setTwoFaToken(data.two_fa_token);
        setBusy(false);
        return;
      }
      if (!data?.user?.is_admin) {
        setErr("Dit account heeft geen admin-rechten.");
        setBusy(false);
        return;
      }
      await checkAuth();
      nav("/admin", { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Inloggen mislukt");
      setErr(msg);
      setBusy(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", {
        two_fa_token: twoFaToken,
        code: twoFaCode.trim().toUpperCase(),
      });
      if (!data?.user?.is_admin) {
        setErr("Dit account heeft geen admin-rechten.");
        setBusy(false);
        return;
      }
      await checkAuth();
      nav("/admin", { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Code klopt niet");
      setErr(msg);
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      // API always returns 200 to prevent enumeration; assume success.
      setNote("Als dit adres een admin-account is, sturen we je nu een reset-code per e-mail.");
      setMode("reset");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Kon reset-e-mail niet versturen — probeer opnieuw.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPassword.length < 8 || !/[A-Za-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setErr("Wachtwoord moet minstens 8 tekens zijn en letters + cijfers bevatten.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        email: email.trim(),
        code: resetCode.trim(),
        new_password: newPassword,
      });
      if (!data?.user?.is_admin) {
        setErr("Wachtwoord gereset, maar dit account heeft geen admin-rechten.");
        setBusy(false);
        return;
      }
      await checkAuth();
      nav("/admin", { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Reset mislukt");
      setErr(msg);
      setBusy(false);
    }
  };

  const backToSignin = () => {
    setMode("signin");
    setErr(""); setNote("");
    setTwoFaToken(""); setTwoFaCode("");
    setResetCode(""); setNewPassword("");
  };

  const activeSubmit = (() => {
    if (twoFaToken) return submit2fa;
    if (mode === "forgot") return submitForgot;
    if (mode === "reset") return submitReset;
    return submit;
  })();

  const submitDisabled = (() => {
    if (busy) return true;
    if (twoFaToken) return twoFaCode.length < 6;
    if (mode === "forgot") return !email;
    if (mode === "reset") return !email || !resetCode || !newPassword;
    return !email || !password;
  })();

  const submitLabel = (() => {
    if (busy) return "Bezig…";
    if (twoFaToken) return "Bevestig code";
    if (mode === "forgot") return "Stuur reset-code";
    if (mode === "reset") return "Wachtwoord instellen";
    return "Log in als admin";
  })();

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>

        <div className="bg-white border border-neutral-200 p-8" data-testid="admin-login-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-neutral-900 text-[#F9F8F6] flex items-center justify-center">
              <ShieldCheck size={18} strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">Beheer</p>
              <h1 className="font-serif text-2xl leading-tight">
                {mode === "forgot" ? "Wachtwoord vergeten" : mode === "reset" ? "Nieuwe wachtwoord" : "Admin inloggen"}
              </h1>
            </div>
          </div>
          <p className="text-sm text-neutral-600 mt-3 leading-relaxed">
            {mode === "forgot"
              ? "Voer je admin-e-mail in — we sturen je een 6-cijferige reset-code."
              : mode === "reset"
              ? "Vul de code uit de e-mail in en kies een nieuw wachtwoord (min. 8 tekens, letters + cijfers)."
              : "Aparte inlog voor beheertoegang — niet gekoppeld aan Google-login."}
          </p>

          <form onSubmit={activeSubmit} className="mt-6 space-y-4">
            {/* --- Signin fields --- */}
            {!twoFaToken && mode === "signin" && (
              <>
                <label className="block">
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 block mb-1.5">
                    Admin-e-mail
                  </span>
                  <input
                    type="email"
                    required
                    autoFocus
                    data-testid="admin-login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-neutral-300 px-3 py-2.5 text-sm focus:border-neutral-900 outline-none"
                    placeholder="jij@voorbeeld.nl"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 block mb-1.5">
                    Wachtwoord
                  </span>
                  <input
                    type="password"
                    required
                    data-testid="admin-login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full border border-neutral-300 px-3 py-2.5 text-sm focus:border-neutral-900 outline-none"
                    placeholder="••••••••"
                  />
                </label>
                <div className="text-right">
                  <button
                    type="button"
                    data-testid="admin-login-forgot-link"
                    onClick={() => { setErr(""); setNote(""); setMode("forgot"); }}
                    className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 underline underline-offset-2"
                  >
                    Wachtwoord vergeten?
                  </button>
                </div>
              </>
            )}

            {/* --- Forgot: just email --- */}
            {!twoFaToken && mode === "forgot" && (
              <label className="block">
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 block mb-1.5">
                  Admin-e-mail
                </span>
                <input
                  type="email"
                  required
                  autoFocus
                  data-testid="admin-forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-neutral-300 px-3 py-2.5 text-sm focus:border-neutral-900 outline-none"
                  placeholder="jij@voorbeeld.nl"
                />
              </label>
            )}

            {/* --- Reset: code + new password --- */}
            {!twoFaToken && mode === "reset" && (
              <>
                <label className="block">
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 block mb-1.5">
                    Reset-code
                  </span>
                  <input
                    type="text"
                    required
                    autoFocus
                    data-testid="admin-reset-code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\s/g, ""))}
                    maxLength={12}
                    className="w-full border border-neutral-300 px-3 py-3 text-center font-mono text-xl tracking-[0.35em] focus:border-neutral-900 outline-none"
                    placeholder="123456"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 block mb-1.5">
                    Nieuw wachtwoord
                  </span>
                  <input
                    type="password"
                    required
                    data-testid="admin-reset-new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full border border-neutral-300 px-3 py-2.5 text-sm focus:border-neutral-900 outline-none"
                    placeholder="Min. 8 tekens, letters + cijfers"
                  />
                </label>
              </>
            )}

            {/* --- 2FA step-up --- */}
            {twoFaToken && (
              <div data-testid="admin-login-2fa-step">
                <div className="flex items-center gap-2 mb-3">
                  <KeyRound size={14} strokeWidth={1.5} className="text-neutral-600" />
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-600">
                    Tweede stap · authenticatie-app
                  </span>
                </div>
                <p className="text-sm text-neutral-700 mb-4 leading-relaxed">
                  Open je authenticatie-app (Google Authenticator, Authy, 1Password) en voer de 6-cijferige code in — of gebruik een van je backup-codes.
                </p>
                <input
                  type="text"
                  required
                  autoFocus
                  data-testid="admin-login-2fa-code"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value.replace(/\s/g, ""))}
                  maxLength={8}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  className="w-full border border-neutral-300 px-3 py-3 text-center font-mono text-2xl tracking-[0.5em] focus:border-neutral-900 outline-none"
                  placeholder="000000"
                />
              </div>
            )}

            {note && (
              <div className="flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="admin-login-note">
                <MailCheck size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                <span>{note}</span>
              </div>
            )}

            {err && (
              <div className="flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">
                <AlertCircle size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitDisabled}
              data-testid="admin-login-submit"
              className="w-full ed-btn inline-flex items-center justify-center gap-2 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-50"
            >
              {twoFaToken ? <KeyRound size={13} strokeWidth={1.75} /> : <LogIn size={13} strokeWidth={1.75} />}
              {submitLabel}
            </button>

            {(twoFaToken || mode !== "signin") && (
              <button
                type="button"
                data-testid="admin-login-back-to-signin"
                onClick={backToSignin}
                className="w-full text-center font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 pt-2"
              >
                ← Terug naar inloggen
              </button>
            )}
          </form>
        </div>

        <p className="text-center mt-6 font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">
          Geen admin? <a href="/" className="text-neutral-900 hover:underline">Terug naar de site →</a>
        </p>
      </div>
    </div>
  );
}
